import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React, { type ReactElement } from 'react'

import Newsletter from '@/components/centered-call-to-action/centered-call-to-action'
import HeaderEpisode from '@/components/models/episode/header-episode/header-episode'
import MainEpisode from '@/components/models/episode/main-episode/main-episode'
import Related from '@/components/related/related'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import Layout from '@/layouts/layout'
import { filterAndSortCards } from '@/utils/sort'

export type DataProps = {
  datoCmsPctEpisode: Queries.EpisodeQuery['datoCmsPctEpisode']
  allDatoCmsPctPost: Queries.EpisodeQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.EpisodeQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.EpisodeQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.EpisodeQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.EpisodeQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.EpisodeQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.EpisodeQuery['allDatoCmsPctPage']
  allDatoCmsPctExternalContent: Queries.EpisodeQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctEvent: Queries.EpisodeQuery['allDatoCmsPctEvent']
}

export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctEpisode },
  location
}): ReactElement => {
  return (
    <>
      <Seo data={datoCmsPctEpisode} location={location} />
      <Microdata data={datoCmsPctEpisode} location={location} />
    </>
  )
}
const Episode: React.FC<PageProps<DataProps>> = ({
  data: {
    datoCmsPctEpisode,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctPage,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  location
}) => {
  const relatedContents = filterAndSortCards([
    ...(allDatoCmsPctPost.nodes ?? []),
    ...(allDatoCmsPctInterview.nodes ?? []),
    ...(allDatoCmsPctPaper.nodes ?? []),
    ...(allDatoCmsPctDialog.nodes ?? []),
    ...(allDatoCmsPctProject.nodes ?? []),
    ...(allDatoCmsPctPage.nodes ?? []),
    ...(allDatoCmsPctExternalContent.nodes ?? []),
    ...(allDatoCmsPctEvent.nodes ?? [])
  ] as Card[]).slice(0, 3)
  const relatedEpisodes = filterAndSortCards([
    ...allDatoCmsPctEpisode.nodes
  ]).slice(0, 3)
  return (
    <Layout location={location} showNavigation>
      <article>
        <HeaderEpisode episode={datoCmsPctEpisode} />
        <MainEpisode episode={datoCmsPctEpisode} />
        <Related related={relatedEpisodes} title="Other Episodes" />
        <Related related={relatedContents} />
      </article>
      <Newsletter />
    </Layout>
  )
}

export default Episode

export const query = graphql`
  query Episode($id: String, $related: [String] = "", $other: [String] = "") {
    datoCmsPctEpisode(id: { eq: $id }) {
      ...PctEpisode
    }

    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPageCard
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related }, showInWebsite: { eq: true } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }
    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $other } }
    ) {
      nodes {
        ...PctEpisodeCard
      }
    }
  }

  fragment PctEpisode on DatoCmsPctEpisode {
    __typename
    originalId
    slug
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    seo {
      title
      description
      image {
        url
      }
    }
    meta {
      firstPublishedAt
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    externalName
    excerpt
    image {
      ...imgHeaderEpisode
    }
    card: image {
      ...imgCard
    }
    content {
      value
      links {
        __typename
        ... on DatoCmsPctDialog {
          ...PctDialogCardInline
        }
        ... on DatoCmsPctTopic {
          ...PctTopicCardInline
        }
        ... on DatoCmsPctPaper {
          ...PctPaperCardInline
        }
        ... on DatoCmsPctInternalPage {
          ...PctInternalPageCardInline
        }
        ... on DatoCmsPctPost {
          ...PctPostCardInline
        }
        ... on DatoCmsPctInterview {
          ...PctInterviewCardInline
        }
        ... on DatoCmsPctProject {
          ...PctProjectCardInline
        }
        ... on DatoCmsPctPage {
          ...PctPageCardInline
        }
        ... on DatoCmsPctTag {
          ...PctTagCardInline
        }
        ... on DatoCmsPctReport {
          ...PctReportCardInline
        }
        ... on DatoCmsPctEpisode {
          ...PctEpisodeCardInline
        }
        ... on DatoCmsPctLink {
          ...PctLink
        }
      }
      blocks {
        __typename
        ... on DatoCmsImage {
          ...ImageInline
        }
        ... on DatoCmsVideo {
          ...VideoInline
        }
        ... on DatoCmsEmbed {
          ...EmbedInline
        }
        ... on DatoCmsForm {
          ...FormInline
        }
      }
    }
    urlProvider
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      ...Author
    }
    guest {
      ...Author
    }
  }
`
